"use client";

import { useEffect, useMemo, useState } from "react";
import {
  Activity,
  Bot,
  Check,
  CheckCircle2,
  ChevronRight,
  Circle,
  Clock3,
  GitBranch,
  Loader2,
  Radio,
  Signal,
  SignalLow,
  Sparkles,
  X,
  XCircle,
} from "lucide-react";
import { useStore } from "@/lib/store";
import type { AgentRun, AgentRunPlanStep, Project } from "@/lib/types";
import {
  childRuns,
  currentPlanStep,
  liveRunSignal,
  partitionLiveRuns,
  planProgress,
  rootRuns,
  type LiveRunSignal,
} from "@/lib/liveAgentRuns";
import { cn } from "@/lib/utils";
import { formatRelativeTime } from "@/lib/timezone";
import { Avatar } from "@/components/ui/Avatar";
import { Drawer } from "@/components/ui/Drawer";
import { Markdown } from "@/components/ui/Markdown";

const RECENT_WINDOW_MS = 24 * 60 * 60_000;

export function LiveWorkspaceView() {
  const activeWorkspaceId = useStore((state) => state.activeWorkspaceId);
  const allRuns = useStore((state) => state.agentRuns);
  const projects = useStore((state) => state.projects);
  const [selectedRunId, setSelectedRunId] = useState<string | null>(null);
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 1_000);
    return () => window.clearInterval(timer);
  }, []);

  const runs = useMemo(
    () =>
      allRuns
        .filter((run) => run.workspaceId === activeWorkspaceId)
        .sort((a, b) => b.startedAt - a.startedAt),
    [activeWorkspaceId, allRuns]
  );
  const roots = useMemo(() => rootRuns(runs), [runs]);
  const { activeRoots: active, staleRoots, working, quiet, stale } = useMemo(() => partitionLiveRuns(runs, now), [runs, now]);
  const recentOutcomes = useMemo(
    () =>
      roots
        .filter(
          (run) =>
            run.status !== "in_progress" &&
            (run.endedAt ?? run.updatedAt ?? run.startedAt) >=
              now - RECENT_WINDOW_MS
        ),
    [now, roots]
  );
  const recent = useMemo(() => recentOutcomes.slice(0, 12), [recentOutcomes]);
  const grouped = useMemo(
    () => groupRunsByProject(active, projects, activeWorkspaceId),
    [active, projects, activeWorkspaceId]
  );
  const selectedRun = runs.find((run) => run.id === selectedRunId) ?? null;
  const selectedChildren = selectedRun ? childRuns(runs, selectedRun.id) : [];
  const delivered = recentOutcomes.filter(
    (run) => run.status === "completed"
  ).length;

  return (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col">
      <div className="shrink-0 border-b border-[var(--border)] bg-[var(--bg-secondary)]">
        <div className="mx-auto w-full max-w-[1280px] px-5 py-4 md:px-6">
          <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
            <div>
              <div className="flex items-center gap-2">
                <Radio size={15} className="text-[var(--accent)]" />
                <h2 className="text-[20px] font-medium tracking-tight text-[var(--fg)]">
                  Live workspace
                </h2>
              </div>
              <p className="mt-1 text-[12px] text-[var(--fg-tertiary)]">
                What agents are doing now, the current step, and what just landed.
              </p>
            </div>
            <div className="flex items-center gap-2 text-[10.5px] text-[var(--fg-muted)]">
              <Signal size={11} />
              Realtime · updates after meaningful work signals
            </div>
          </div>

          <div className="mt-4 grid grid-cols-2 gap-2 md:grid-cols-4">
            <SignalCard label="Recent signal" value={working} tone="green" />
            <SignalCard label="Quiet" value={quiet} tone="yellow" />
            <SignalCard label="No recent signal" value={stale} tone="red" />
            <SignalCard label="Delivered · 24h" value={delivered} tone="accent" />
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto scrollbar-thin">
        <div className="mx-auto w-full max-w-[1280px] space-y-6 px-5 py-5 md:px-6">
          {active.length === 0 ? (
            <EmptyLiveState recentCount={recent.length} />
          ) : (
            grouped.map((group) => (
              <section key={group.key}>
                <div className="mb-2 flex items-center gap-2">
                  <span
                    className="flex h-6 w-6 items-center justify-center rounded border border-[var(--border)] bg-[var(--bg-tertiary)] text-[12px]"
                    style={
                      group.project
                        ? {
                            color: group.project.color,
                            borderColor: `${group.project.color}55`,
                          }
                        : undefined
                    }
                  >
                    {group.project?.icon ?? "◇"}
                  </span>
                  <h3 className="text-[12.5px] font-medium text-[var(--fg)]">
                    {group.project?.name ?? "Unassigned work"}
                  </h3>
                  <span className="text-[10.5px] text-[var(--fg-muted)]">
                    {group.runs.length} with recent signal
                  </span>
                </div>
                <div className="grid gap-3 xl:grid-cols-2">
                  {group.runs.map((run) => (
                    <LiveRunCard
                      key={run.id}
                      run={run}
                      children={childRuns(runs, run.id)}
                      now={now}
                      onOpen={() => setSelectedRunId(run.id)}
                    />
                  ))}
                </div>
              </section>
            ))
          )}

          {staleRoots.length > 0 && <details className="rounded-md border border-[var(--border)] p-4">
            <summary className="focus-ring min-h-11 cursor-pointer text-[12.5px] font-medium text-[var(--fg)]">No recent signal · {staleRoots.length} runs</summary>
            <p className="mb-3 text-[12px] leading-5 text-[var(--fg-tertiary)]">No signal for at least 15 minutes. Outcomes are unconfirmed; tasks and execution history are preserved. A fresh signal returns the run to the live list.</p>
            <div className="grid gap-3 xl:grid-cols-2">{staleRoots.map((run) => <LiveRunCard key={run.id} run={run} children={childRuns(runs, run.id)} now={now} onOpen={() => setSelectedRunId(run.id)} />)}</div>
          </details>}

          <section>
            <div className="mb-2 flex items-center gap-2">
              <CheckCircle2 size={13} className="text-[var(--fg-tertiary)]" />
              <h3 className="text-[12.5px] font-medium text-[var(--fg)]">
                Recent outcomes
              </h3>
              <span className="text-[10.5px] text-[var(--fg-muted)]">
                Last 24 hours
              </span>
            </div>
            {recent.length === 0 ? (
              <div className="rounded-md border border-dashed border-[var(--border)] px-4 py-6 text-center text-[11.5px] text-[var(--fg-muted)]">
                Completed and failed runs will collect here.
              </div>
            ) : (
              <div className="overflow-hidden rounded-md border border-[var(--border)] bg-[var(--bg-secondary)]">
                {recent.map((run) => (
                  <RecentRunRow
                    key={run.id}
                    run={run}
                    onOpen={() => setSelectedRunId(run.id)}
                  />
                ))}
              </div>
            )}
          </section>
        </div>
      </div>

      <LiveRunDrawer
        run={selectedRun}
        children={selectedChildren}
        now={now}
        onClose={() => setSelectedRunId(null)}
      />
    </div>
  );
}

function SignalCard({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone: "green" | "yellow" | "red" | "accent";
}) {
  const color =
    tone === "green"
      ? "var(--green)"
      : tone === "yellow"
        ? "var(--yellow)"
        : tone === "red"
          ? "var(--red)"
          : "var(--accent)";
  return (
    <div className="rounded-md border border-[var(--border)] bg-[var(--bg-tertiary)] px-3 py-2.5">
      <div className="text-[18px] font-medium tracking-tight" style={{ color }}>
        {value}
      </div>
      <div className="mt-0.5 text-[10.5px] text-[var(--fg-tertiary)]">
        {label}
      </div>
    </div>
  );
}

function LiveRunCard({
  run,
  children,
  now,
  onOpen,
}: {
  run: AgentRun;
  children: AgentRun[];
  now: number;
  onOpen: () => void;
}) {
  const users = useStore((state) => state.users);
  const sessions = useStore((state) => state.agentSessions);
  const actor = users.find((user) => user.id === run.actorId);
  const session = sessions.find((candidate) => candidate.id === run.sessionId);
  const signal = liveRunSignal(run, now);
  const progress = planProgress(run);
  const activeStep = currentPlanStep(run);
  const branch =
    typeof session?.context?.branch === "string" ? session.context.branch : null;
  const activeChildren = children.filter(
    (child) => child.status === "in_progress"
  );
  const agentName = run.agentLabel || actor?.name || "Agent";

  return (
    <button
      type="button"
      onClick={onOpen}
      aria-label={liveRunCardAriaLabel(run, agentName, activeChildren.length)}
      className="group min-w-0 w-full rounded-md border border-[var(--border)] bg-[var(--bg-secondary)] p-4 text-left transition-colors hover:border-[var(--border-strong)] hover:bg-[var(--bg-hover)] focus-ring"
    >
      <div className="flex items-start gap-3">
        {actor ? (
          <Avatar user={actor} size={28} />
        ) : (
          <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded bg-[var(--bg-tertiary)] text-[var(--fg-tertiary)]">
            <Bot size={13} />
          </span>
        )}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="truncate text-[12px] font-medium text-[var(--fg)]">
              {agentName}
            </span>
            <LiveSignalChip signal={signal} />
            <span className="ml-auto shrink-0 text-[10.5px] text-[var(--fg-muted)]">
              {formatElapsed(run.startedAt, now)}
            </span>
          </div>
          <h4 className="mt-2 line-clamp-2 text-[13.5px] font-medium leading-5 text-[var(--fg)]">
            {runTitle(run)}
          </h4>
          <div className="mt-3 rounded-md border border-[var(--border)] bg-[var(--bg-tertiary)] px-3 py-2.5">
            <div className="flex items-center gap-2">
              {signal === "working" ? (
                <Loader2
                  size={12}
                  className="animate-spin text-[var(--accent)] motion-reduce:animate-none"
                />
              ) : (
                <SignalLow size={12} className="text-[var(--fg-muted)]" />
              )}
              <span className="min-w-0 flex-1 truncate text-[11.5px] text-[var(--fg-secondary)]">
                {run.currentStep ||
                  activeStep?.label ||
                  (signal === "stale" ? "Outcome not confirmed" : "Awaiting the next work signal")}
              </span>
              {progress.total > 0 && (
                <span className="shrink-0 text-[10.5px] text-[var(--fg-muted)]">
                  {progress.completed} of {progress.total}
                </span>
              )}
            </div>
          </div>
          {activeChildren.length > 0 && (
            <div className="mt-2 overflow-hidden rounded-md border border-[var(--border)] bg-[var(--bg-tertiary)]">
              {activeChildren.slice(0, 3).map((child) => {
                const childSignal = liveRunSignal(child, now);
                return (
                  <div
                    key={child.id}
                    className="flex min-w-0 items-center gap-2 border-b border-[var(--border)] px-2.5 py-2 last:border-b-0"
                  >
                    <LiveSignalDot signal={childSignal} />
                    <span className="min-w-0 max-w-[40%] truncate text-[10.5px] font-medium text-[var(--fg-secondary)]">
                      {child.agentLabel || displayRunSource(child.source)}
                    </span>
                    <span className="min-w-0 flex-1 truncate text-right text-[10.5px] text-[var(--fg-muted)]">
                      {child.currentStep || child.summary || (childSignal === "stale" ? "Outcome not confirmed" : "Recent signal")}
                    </span>
                  </div>
                );
              })}
              {activeChildren.length > 3 && (
                <div className="px-2.5 py-1.5 text-[10.5px] text-[var(--fg-muted)]">
                  +{activeChildren.length - 3} more active
                </div>
              )}
            </div>
          )}
          <div className="mt-3 flex flex-wrap items-center gap-2 text-[10.5px] text-[var(--fg-muted)]">
            {branch && (
              <span className="inline-flex items-center gap-1">
                <GitBranch size={10} /> {branch}
              </span>
            )}
            {activeChildren.length > 0 && (
              <span className="inline-flex items-center gap-1 text-[var(--accent)]">
                <Sparkles size={10} /> {activeChildren.length} subagent
                {activeChildren.length === 1 ? "" : "s"}
              </span>
            )}
            <span className="ml-auto inline-flex items-center gap-1 text-[var(--fg-tertiary)]">
              Inspect <ChevronRight size={10} />
            </span>
          </div>
        </div>
      </div>
    </button>
  );
}

function RecentRunRow({
  run,
  onOpen,
}: {
  run: AgentRun;
  onOpen: () => void;
}) {
  const users = useStore((state) => state.users);
  const actor = users.find((user) => user.id === run.actorId);
  const completed = run.status === "completed";
  return (
    <button
      type="button"
      onClick={onOpen}
      className="group flex w-full items-center gap-3 border-b border-[var(--border)] px-3 py-2.5 text-left last:border-b-0 hover:bg-[var(--bg-hover)] transition-colors focus-ring"
    >
      {completed ? (
        <CheckCircle2 size={13} className="shrink-0 text-[var(--green)]" />
      ) : (
        <XCircle size={13} className="shrink-0 text-[var(--red)]" />
      )}
      <span className="min-w-0 flex-1 truncate text-[12px] text-[var(--fg-secondary)]">
        {runTitle(run)}
      </span>
      <span className="hidden shrink-0 text-[10.5px] text-[var(--fg-muted)] sm:block">
        {run.agentLabel || actor?.name || "Agent"}
      </span>
      <span className="shrink-0 text-[10.5px] text-[var(--fg-muted)]">
        {formatRelativeTime(run.endedAt ?? run.updatedAt ?? run.startedAt)}
      </span>
      <ChevronRight
        size={11}
        className="shrink-0 text-[var(--fg-muted)] group-hover:text-[var(--fg-secondary)]"
      />
    </button>
  );
}

function LiveRunDrawer({
  run,
  children,
  now,
  onClose,
}: {
  run: AgentRun | null;
  children: AgentRun[];
  now: number;
  onClose: () => void;
}) {
  const users = useStore((state) => state.users);
  const sessions = useStore((state) => state.agentSessions);
  const issues = useStore((state) => state.issues);
  const actor = run ? users.find((user) => user.id === run.actorId) : undefined;
  const session = run
    ? sessions.find((candidate) => candidate.id === run.sessionId)
    : undefined;
  const runIssues = run
    ? issues.filter((issue) => run.touchedIssueIds.includes(issue.id))
    : [];
  const progress = run ? planProgress(run) : { completed: 0, total: 0 };
  const planLabels = new Set(run?.planSteps?.map((step) => step.label) ?? []);
  const recordedActions = run
    ? run.actions.filter((action) => !planLabels.has(action))
    : [];
  const titleId = "live-run-drawer-title";

  return (
    <Drawer
      open={Boolean(run)}
      onClose={onClose}
      width={680}
      ariaLabelledBy={titleId}
    >
      {run && (
        <>
          <div className="flex items-start gap-3 border-b border-[var(--border)] px-5 py-4">
            {actor ? (
              <Avatar user={actor} size={30} />
            ) : (
              <span className="flex h-[30px] w-[30px] items-center justify-center rounded bg-[var(--bg-tertiary)]">
                <Bot size={14} />
              </span>
            )}
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <span className="text-[11px] text-[var(--fg-tertiary)]">
                  {run.agentLabel || actor?.name || "Agent"}
                </span>
                <RunStatusChip run={run} now={now} />
              </div>
              <h2
                id={titleId}
                className="mt-1 text-[18px] font-medium leading-6 tracking-tight text-[var(--fg)]"
              >
                {runTitle(run)}
              </h2>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-[var(--fg-tertiary)] hover:bg-[var(--bg-hover)] hover:text-[var(--fg)] transition-colors focus-ring"
              aria-label="Close live run"
            >
              <X size={14} />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto scrollbar-thin px-5 py-5">
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
              <DrawerMetric
                label="Elapsed"
                value={formatElapsed(run.startedAt, run.endedAt ?? now)}
              />
              <DrawerMetric
                label="Plan"
                value={
                  progress.total > 0
                    ? `${progress.completed} of ${progress.total}`
                    : "Not published"
                }
              />
              <DrawerMetric
                label="Subagents"
                value={String(children.length)}
              />
              <DrawerMetric
                label="Last signal"
                value={formatRelativeTime(
                  run.heartbeatAt ?? run.updatedAt ?? run.startedAt
                )}
              />
            </div>

            <DrawerSection title="Objective">
              <p className="whitespace-pre-wrap text-[12.5px] leading-relaxed text-[var(--fg-secondary)]">
                {run.prompt}
              </p>
            </DrawerSection>

            <DrawerSection title="Plan">
              {(run.planSteps?.length ?? 0) > 0 ? (
                <div className="overflow-hidden rounded-md border border-[var(--border)] bg-[var(--bg-secondary)]">
                  {run.planSteps?.map((step) => (
                    <PlanStepRow key={step.id} step={step} />
                  ))}
                </div>
              ) : (
                <EmptyDrawerCopy>
                  The agent has not published a structured plan for this run.
                </EmptyDrawerCopy>
              )}
            </DrawerSection>

            {recordedActions.length > 0 && (
              <DrawerSection title="Actions taken">
                <ul className="space-y-1.5 rounded-md border border-[var(--border)] bg-[var(--bg-secondary)] px-3 py-2.5">
                  {recordedActions.map((action, index) => (
                    <li
                      key={`${index}-${action}`}
                      className="flex gap-2 text-[11.5px] leading-5 text-[var(--fg-secondary)]"
                    >
                      <Check
                        size={11}
                        className="mt-1 shrink-0 text-[var(--green)]"
                      />
                      <span>{action}</span>
                    </li>
                  ))}
                </ul>
              </DrawerSection>
            )}

            {children.length > 0 && (
              <DrawerSection title="Subagents">
                <div className="space-y-2">
                  {children.map((child) => (
                    <div
                      key={child.id}
                      className="rounded-md border border-[var(--border)] bg-[var(--bg-secondary)] px-3 py-2.5"
                    >
                      <div className="flex items-center gap-2">
                        <Bot size={11} className="text-[var(--accent)]" />
                        <span className="text-[11.5px] font-medium text-[var(--fg)]">
                          {child.agentLabel || "Subagent"}
                        </span>
                        <RunStatusChip run={child} now={now} />
                        <span className="ml-auto text-[10.5px] text-[var(--fg-muted)]">
                          {formatElapsed(child.startedAt, child.endedAt ?? now)}
                        </span>
                      </div>
                      <p className="mt-1.5 line-clamp-2 text-[11.5px] text-[var(--fg-tertiary)]">
                        {child.currentStep || child.summary || child.prompt}
                      </p>
                    </div>
                  ))}
                </div>
              </DrawerSection>
            )}

            {runIssues.length > 0 && (
              <DrawerSection title="Linked tasks">
                <div className="flex flex-wrap gap-2">
                  {runIssues.map((issue) => (
                    <span
                      key={issue.id}
                      className="rounded border border-[var(--border)] bg-[var(--bg-tertiary)] px-2 py-1 text-[10.5px] text-[var(--fg-secondary)]"
                    >
                      {issue.identifier} · {issue.title}
                    </span>
                  ))}
                </div>
              </DrawerSection>
            )}

            {run.summary.trim() && (
              <DrawerSection
                title={run.status === "in_progress" ? "Latest update" : "Outcome"}
              >
                <div className="prose-doc text-[12.5px] leading-relaxed text-[var(--fg-secondary)]">
                  <Markdown content={run.summary} />
                </div>
              </DrawerSection>
            )}

            <DrawerSection title="Execution context">
              <div className="overflow-hidden rounded-md border border-[var(--border)] bg-[var(--bg-secondary)]">
                <ContextRow label="Session" value={session?.name ?? "Unknown"} />
                <ContextRow
                  label="Source"
                  value={displayRunSource(run.source)}
                />
                {typeof session?.context?.branch === "string" && (
                  <ContextRow label="Branch" value={session.context.branch} />
                )}
                {typeof session?.context?.cwd === "string" && (
                  <ContextRow
                    label="Workspace"
                    value={displayWorkspaceName(session.context.cwd)}
                  />
                )}
              </div>
            </DrawerSection>
          </div>
        </>
      )}
    </Drawer>
  );
}

function DrawerSection({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section className="mt-6">
      <h3 className="mb-2 text-[10.5px] font-medium uppercase tracking-wider text-[var(--fg-muted)]">
        {title}
      </h3>
      {children}
    </section>
  );
}

function DrawerMetric({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md border border-[var(--border)] bg-[var(--bg-secondary)] px-3 py-2">
      <div className="truncate text-[11.5px] font-medium text-[var(--fg)]">
        {value}
      </div>
      <div className="mt-0.5 text-[10px] text-[var(--fg-muted)]">{label}</div>
    </div>
  );
}

function PlanStepRow({ step }: { step: AgentRunPlanStep }) {
  return (
    <div className="flex items-start gap-2 border-b border-[var(--border)] px-3 py-2.5 last:border-b-0">
      {step.status === "completed" ? (
        <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-[var(--green)] text-white">
          <Check size={10} />
        </span>
      ) : step.status === "in_progress" ? (
        <Loader2
          size={14}
          className="mt-0.5 shrink-0 animate-spin text-[var(--accent)] motion-reduce:animate-none"
        />
      ) : (
        <Circle size={14} className="mt-0.5 shrink-0 text-[var(--fg-muted)]" />
      )}
      <span
        className={cn(
          "text-[12px] leading-5",
          step.status === "completed"
            ? "text-[var(--fg-tertiary)]"
            : "text-[var(--fg-secondary)]"
        )}
      >
        {step.label}
      </span>
    </div>
  );
}

function ContextRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-start gap-3 border-b border-[var(--border)] px-3 py-2.5 last:border-b-0">
      <span className="w-20 shrink-0 text-[10.5px] text-[var(--fg-muted)]">
        {label}
      </span>
      <span className="min-w-0 flex-1 break-all text-[11.5px] text-[var(--fg-secondary)]">
        {value}
      </span>
    </div>
  );
}

function EmptyDrawerCopy({ children }: { children: React.ReactNode }) {
  return (
    <div className="rounded-md border border-dashed border-[var(--border)] px-3 py-4 text-[11.5px] text-[var(--fg-muted)]">
      {children}
    </div>
  );
}

function RunStatusChip({ run, now }: { run: AgentRun; now: number }) {
  if (run.status === "completed") {
    return <StatusChip label="Completed" color="var(--green)" />;
  }
  if (run.status === "failed") {
    return <StatusChip label="Failed" color="var(--red)" />;
  }
  return <LiveSignalChip signal={liveRunSignal(run, now)} />;
}

function LiveSignalChip({ signal }: { signal: LiveRunSignal }) {
  if (signal === "working") {
    return <StatusChip label="Working" color="var(--green)" pulse />;
  }
  if (signal === "quiet") {
    return <StatusChip label="Quiet" color="var(--yellow)" />;
  }
  return <StatusChip label="No signal" color="var(--red)" />;
}

function LiveSignalDot({ signal }: { signal: LiveRunSignal }) {
  const color =
    signal === "working"
      ? "var(--green)"
      : signal === "quiet"
        ? "var(--yellow)"
        : "var(--red)";
  return (
    <span className="relative flex h-1.5 w-1.5 shrink-0" aria-hidden="true">
      {signal === "working" && (
        <span
          className="absolute inline-flex h-full w-full animate-ping rounded-full opacity-60 motion-reduce:animate-none"
          style={{ background: color }}
        />
      )}
      <span
        className="relative inline-flex h-1.5 w-1.5 rounded-full"
        style={{ background: color }}
      />
    </span>
  );
}

function StatusChip({
  label,
  color,
  pulse = false,
}: {
  label: string;
  color: string;
  pulse?: boolean;
}) {
  return (
    <span
      className="inline-flex shrink-0 items-center gap-1 rounded px-1.5 py-0.5 text-[9.5px] font-medium"
      style={{
        color,
        background: `color-mix(in srgb, ${color} 12%, transparent)`,
      }}
    >
      <span className="relative flex h-1.5 w-1.5">
        {pulse && (
          <span
            className="absolute inline-flex h-full w-full animate-ping rounded-full opacity-60 motion-reduce:animate-none"
            style={{ background: color }}
          />
        )}
        <span
          className="relative inline-flex h-1.5 w-1.5 rounded-full"
          style={{ background: color }}
        />
      </span>
      {label}
    </span>
  );
}

function EmptyLiveState({ recentCount }: { recentCount: number }) {
  return (
    <div className="rounded-md border border-dashed border-[var(--border)] px-6 py-10 text-center">
      <Activity
        size={24}
        className="mx-auto text-[var(--fg-muted)] opacity-60"
      />
      <h3 className="mt-3 text-[13px] font-medium text-[var(--fg)]">
        The workspace is quiet
      </h3>
      <p className="mx-auto mt-1 max-w-md text-[11.5px] leading-relaxed text-[var(--fg-muted)]">
        New Codex and Claude turns will appear here as soon as the Pauta Live
        hook reports their first meaningful step.
        {recentCount > 0 ? " Recent outcomes are still visible below." : ""}
      </p>
    </div>
  );
}

function groupRunsByProject(
  runs: AgentRun[],
  projects: Project[],
  workspaceId: string
) {
  const projectById = new Map(
    projects
      .filter((project) => project.workspaceId === workspaceId)
      .map((project) => [project.id, project])
  );
  const groups = new Map<
    string,
    { key: string; project: Project | null; runs: AgentRun[] }
  >();
  for (const run of runs) {
    const project = run.projectIds
      .map((id) => projectById.get(id))
      .find((candidate): candidate is Project => Boolean(candidate));
    const key = project?.id ?? "unassigned";
    const group = groups.get(key) ?? {
      key,
      project: project ?? null,
      runs: [],
    };
    group.runs.push(run);
    groups.set(key, group);
  }
  return [...groups.values()].sort((a, b) => {
    if (a.project && !b.project) return -1;
    if (!a.project && b.project) return 1;
    return (
      Math.max(...b.runs.map((run) => run.startedAt)) -
      Math.max(...a.runs.map((run) => run.startedAt))
    );
  });
}

function runTitle(run: AgentRun) {
  return (
    run.prompt
      .split(/\r?\n/)
      .map((line) => line.trim())
      .find(Boolean) ?? "Untitled agent task"
  );
}

function liveRunCardAriaLabel(
  run: AgentRun,
  agentName: string,
  activeChildCount: number
) {
  const title = runTitle(run).slice(0, 160);
  const childSummary =
    activeChildCount > 0
      ? ` ${activeChildCount} active ${
          activeChildCount === 1 ? "subagent" : "subagents"
        }.`
      : "";
  return `Inspect run: ${title}. Agent: ${agentName}.${childSummary}`;
}

function displayWorkspaceName(value: string) {
  const segments = value.split(/[\\/]/).filter(Boolean);
  return segments.slice(-2).join("/") || "Workspace";
}

function displayRunSource(source: string | null | undefined) {
  if (source === "codex") return "Codex";
  if (source === "claude" || source === "claude_code") return "Claude";
  return source || "Agent";
}

function formatElapsed(startedAt: number, endedAt: number) {
  const totalSeconds = Math.max(0, Math.floor((endedAt - startedAt) / 1_000));
  if (totalSeconds < 60) return `${totalSeconds}s`;
  const minutes = Math.floor(totalSeconds / 60);
  if (minutes < 60) return `${minutes}m ${totalSeconds % 60}s`;
  const hours = Math.floor(minutes / 60);
  return `${hours}h ${minutes % 60}m`;
}
